/**
 * Formula Helper Utilities
 *
 * Provides constants, validation, and parsing utilities for formula building.
 * Supports HyperFormula-like syntax with column references and operators.
 */

import { removeStringLiterals } from '@loader/shared-types';

// Re-export for backwards compatibility with existing imports
export { removeStringLiterals };

/**
 * Supported formula operators with descriptions and examples
 */
export const FORMULA_OPERATORS = [
  // Arithmetic Operators
  {
    value: '+',
    label: 'Add (+)',
    description: 'Adds two values together',
    example: '[quantity] + [bonus]',
    category: 'Arithmetic',
  },
  {
    value: '-',
    label: 'Subtract (-)',
    description: 'Subtracts the second value from the first',
    example: '[revenue] - [cost]',
    category: 'Arithmetic',
  },
  {
    value: '*',
    label: 'Multiply (*)',
    description: 'Multiplies two values',
    example: '[quantity] * [price]',
    category: 'Arithmetic',
  },
  {
    value: '/',
    label: 'Divide (/)',
    description: 'Divides the first value by the second',
    example: '[total] / [count]',
    category: 'Arithmetic',
  },
  {
    value: '%',
    label: 'Modulo (%)',
    description: 'Returns the remainder of division',
    example: '[value] % 10',
    category: 'Arithmetic',
  },
  {
    value: '^',
    label: 'Power (^)',
    description: 'Raises first value to the power of second',
    example: '[base] ^ 2',
    category: 'Arithmetic',
  },

  // Comparison Operators
  {
    value: '=',
    label: 'Equal (=)',
    description: 'Checks if two values are equal',
    example: '[status] = "active"',
    category: 'Comparison',
  },
  {
    value: '<>',
    label: 'Not Equal (<>)',
    description: 'Checks if two values are not equal',
    example: '[status] <> "inactive"',
    category: 'Comparison',
  },
  {
    value: '>',
    label: 'Greater Than (>)',
    description: 'Checks if first value is greater than second',
    example: '[price] > 100',
    category: 'Comparison',
  },
  {
    value: '<',
    label: 'Less Than (<)',
    description: 'Checks if first value is less than second',
    example: '[age] < 18',
    category: 'Comparison',
  },
  {
    value: '>=',
    label: 'Greater or Equal (>=)',
    description: 'Checks if first value is greater than or equal to second',
    example: '[score] >= 90',
    category: 'Comparison',
  },
  {
    value: '<=',
    label: 'Less or Equal (<=)',
    description: 'Checks if first value is less than or equal to second',
    example: '[age] <= 65',
    category: 'Comparison',
  },

  // Logical Operators
  {
    value: '&',
    label: 'Concatenate (&)',
    description: 'Joins text strings together',
    example: '[first_name] & " " & [last_name]',
    category: 'Text',
  },

  // Functions
  {
    value: 'SUM',
    label: 'SUM',
    description: 'Adds all values in the arguments',
    example: 'SUM([col1], [col2], [col3])',
    category: 'Math',
  },
  {
    value: 'AVERAGE',
    label: 'AVERAGE',
    description: 'Calculates the average of values',
    example: 'AVERAGE([score1], [score2])',
    category: 'Math',
  },
  {
    value: 'MIN',
    label: 'MIN',
    description: 'Returns the smallest value',
    example: 'MIN([price1], [price2])',
    category: 'Math',
  },
  {
    value: 'MAX',
    label: 'MAX',
    description: 'Returns the largest value',
    example: 'MAX([score1], [score2])',
    category: 'Math',
  },
  {
    value: 'ROUND',
    label: 'ROUND',
    description: 'Rounds a number to specified decimal places',
    example: 'ROUND([price], 2)',
    category: 'Math',
  },
  {
    value: 'ABS',
    label: 'ABS',
    description: 'Returns the absolute value',
    example: 'ABS([difference])',
    category: 'Math',
  },
  {
    value: 'IF',
    label: 'IF',
    description: 'Returns one value if condition is true, another if false',
    example: 'IF([score] > 90, "A", "B")',
    category: 'Logical',
  },
  {
    value: 'AND',
    label: 'AND',
    description: 'Returns TRUE if all conditions are true',
    example: 'AND([age] >= 18, [status] = "active")',
    category: 'Logical',
  },
  {
    value: 'OR',
    label: 'OR',
    description: 'Returns TRUE if any condition is true',
    example: 'OR([status] = "active", [status] = "pending")',
    category: 'Logical',
  },
  {
    value: 'NOT',
    label: 'NOT',
    description: 'Reverses the logical value',
    example: 'NOT([is_deleted])',
    category: 'Logical',
  },
  {
    value: 'CONCAT',
    label: 'CONCAT',
    description: 'Joins multiple text strings',
    example: 'CONCAT([first], " ", [last])',
    category: 'Text',
  },
  {
    value: 'UPPER',
    label: 'UPPER',
    description: 'Converts text to uppercase',
    example: 'UPPER([name])',
    category: 'Text',
  },
  {
    value: 'LOWER',
    label: 'LOWER',
    description: 'Converts text to lowercase',
    example: 'LOWER([email])',
    category: 'Text',
  },
  {
    value: 'TRIM',
    label: 'TRIM',
    description: 'Removes extra spaces from text',
    example: 'TRIM([name])',
    category: 'Text',
  },
  {
    value: 'LEN',
    label: 'LEN',
    description: 'Returns the length of text',
    example: 'LEN([description])',
    category: 'Text',
  },
  {
    value: 'LEFT',
    label: 'LEFT',
    description: 'Returns leftmost characters from text',
    example: 'LEFT([code], 3)',
    category: 'Text',
  },
  {
    value: 'RIGHT',
    label: 'RIGHT',
    description: 'Returns rightmost characters from text',
    example: 'RIGHT([code], 3)',
    category: 'Text',
  },
  {
    value: 'ISBLANK',
    label: 'ISBLANK',
    description: 'Checks if a value is empty',
    example: 'ISBLANK([optional_field])',
    category: 'Logical',
  },
  {
    value: 'ISNUMBER',
    label: 'ISNUMBER',
    description: 'Checks if a value is a number',
    example: 'ISNUMBER([quantity])',
    category: 'Logical',
  },
  {
    value: 'COUNTIF',
    label: 'COUNTIF',
    description: 'Counts values that meet a condition',
    example: 'COUNTIF([status], "active")',
    category: 'Math',
  },
  {
    value: 'DATE',
    label: 'DATE',
    description: 'Converts a datetime to date (strips time)',
    example: 'DATE([datetime_field])',
    category: 'Date',
  },
] as const;

/**
 * Formula validation error types
 */
export interface FormulaError {
  type: 'syntax' | 'reference' | 'operator' | 'parenthesis';
  message: string;
  position?: number;
}

/**
 * Validates a formula and returns any errors.
 *
 * @param validFunctionNames - Optional set of valid function names from the backend operator list.
 *   When provided, function validation uses this instead of the hardcoded FORMULA_OPERATORS.
 */
export function validateFormula(
  formula: string,
  availableColumns: Array<{
    id: string;
    name: string;
    table_alias?: string;
  }>,
  validFunctionNames?: Set<string>
): FormulaError[] {
  const errors: FormulaError[] = [];

  if (!formula || formula.trim() === '') {
    return errors;
  }

  // Strip leading = (common Excel habit, not part of the formula syntax)
  const cleanedFormula = formula.startsWith('=') ? formula.slice(1) : formula;

  // Remove string literals before counting brackets/parentheses
  // This prevents false positives from brackets inside strings like "[pending]"
  const formulaWithoutStrings = removeStringLiterals(cleanedFormula);

  // Check for unmatched brackets (using cleaned formula)
  const openBrackets = (formulaWithoutStrings.match(/\[/g) || []).length;
  const closeBrackets = (formulaWithoutStrings.match(/\]/g) || []).length;

  if (openBrackets !== closeBrackets) {
    const diff = Math.abs(openBrackets - closeBrackets);
    const missing = openBrackets > closeBrackets ? 'closing ]' : 'opening [';
    errors.push({
      type: 'syntax',
      message: `Missing ${diff} ${missing} bracket${diff > 1 ? 's' : ''}`,
    });
  }

  // Check for unmatched parentheses (using cleaned formula)
  const openParens = (formulaWithoutStrings.match(/\(/g) || []).length;
  const closeParens = (formulaWithoutStrings.match(/\)/g) || []).length;

  if (openParens !== closeParens) {
    const diff = Math.abs(openParens - closeParens);
    const missing = openParens > closeParens ? 'closing )' : 'opening (';
    errors.push({
      type: 'parenthesis',
      message: `Missing ${diff} ${missing} parenthesis${diff > 1 ? 'es' : ''}`,
    });
  }

  // Extract column references [column_name]
  const columnReferences = cleanedFormula.match(/\[([^\]]+)\]/g) || [];
  const columnNames = new Set(
    availableColumns.flatMap((col) => {
      // Check if names are already prefixed (relationship fields come pre-prefixed from backend)
      const name = col.name;
      const isAlreadyPrefixed = name.includes('.');

      if (isAlreadyPrefixed) {
        // Already prefixed, return as-is
        return [name];
      } else {
        // Add table alias prefix if available
        return [
          name,
          col.table_alias ? `${col.table_alias}.${name}` : null,
        ].filter(Boolean);
      }
    })
  );

  for (const ref of columnReferences) {
    const columnName = ref.slice(1, -1); // Remove [ and ]

    if (!columnNames.has(columnName)) {
      errors.push({
        type: 'reference',
        message: `Unknown column: ${ref}. Check the column name or table prefix.`,
      });
    }
  }

  // Check for invalid function names using dynamic backend list or hardcoded fallback
  const functionPattern = /\b([A-Z][A-Z0-9]*)\s*\(/g;
  const functionMatches = [...cleanedFormula.matchAll(functionPattern)];
  const knownFunctions = validFunctionNames ?? new Set<string>(
    FORMULA_OPERATORS.map((op) => op.value)
  );

  for (const match of functionMatches) {
    const func = match[1];
    if (!knownFunctions.has(func)) {
      errors.push({
        type: 'operator',
        message: `Unknown function: ${func}(). Did you mean ${findClosestFunction(func, knownFunctions)}?`,
        position: match.index,
      });
    }
  }

  // Check for empty brackets
  if (cleanedFormula.includes('[]')) {
    errors.push({
      type: 'syntax',
      message: 'Empty column reference []. Type a column name inside the brackets.',
      position: cleanedFormula.indexOf('[]'),
    });
  }

  // Check for consecutive operators
  const consecutiveMatch = /[\+\-\*\/]{2,}/.exec(cleanedFormula);
  if (consecutiveMatch) {
    errors.push({
      type: 'syntax',
      message: `Unexpected "${consecutiveMatch[0]}": consecutive operators are not allowed.`,
      position: consecutiveMatch.index,
    });
  }

  return errors;
}

/**
 * Find the closest matching function name using prefix/substring similarity
 */
function findClosestFunction(input: string, validNames: Set<string>): string {
  const inputLower = input.toLowerCase();
  let best = '';
  let bestScore = Infinity;

  for (const name of validNames) {
    // Only consider uppercase function names
    if (!/^[A-Z]/.test(name)) continue;

    const nameLower = name.toLowerCase();
    // Simple prefix/substring match scoring
    if (nameLower.startsWith(inputLower) || inputLower.startsWith(nameLower)) {
      const score = Math.abs(name.length - input.length);
      if (score < bestScore) {
        bestScore = score;
        best = name;
      }
    }
  }

  if (!best) {
    // Fallback: find shortest edit distance via common prefix length
    for (const name of validNames) {
      if (!/^[A-Z]/.test(name)) continue;
      let common = 0;
      const min = Math.min(name.length, input.length);
      for (let i = 0; i < min; i++) {
        if (name[i].toLowerCase() === input[i].toLowerCase()) common++;
        else break;
      }
      const score = input.length + name.length - 2 * common;
      if (score < bestScore) {
        bestScore = score;
        best = name;
      }
    }
  }

  return best ? `${best}()` : 'a valid function';
}

